import { useTranslation } from 'react-i18next';
import { CollapsibleNote } from '../CollapsibleNote';
import { useAnalyticsSpareParts } from '../../../hooks/useAnalyticsSpareParts';
import type { SparePartRankingRow } from '../../../services/analyticsService';
import './SparePartsTab.css';

interface SparePartsTabProps {
  year: number;
}

type MetricKey = 'used' | 'sent' | 'usagePct';

const METRIC_LABEL_KEY: Record<MetricKey, string> = {
  used: 'analytics.spareParts.columns.used',
  sent: 'analytics.spareParts.columns.sent',
  usagePct: 'analytics.spareParts.columns.usagePct',
};

interface RankingPanelProps {
  title: string;
  sub?: string;
  rows: SparePartRankingRow[];
  metricKey: MetricKey;
  secondaryKey: MetricKey;
  emptyMessage: string;
  descriptionFor: (row: SparePartRankingRow) => string;
  numberFormatter: Intl.NumberFormat;
  pctFormatter: Intl.NumberFormat;
}

const RankingPanel: React.FC<RankingPanelProps> = ({
  title,
  sub,
  rows,
  metricKey,
  secondaryKey,
  emptyMessage,
  descriptionFor,
  numberFormatter,
  pctFormatter,
}) => {
  const { t } = useTranslation();

  const formatValue = (row: SparePartRankingRow, key: MetricKey): string =>
    key === 'usagePct' ? `${pctFormatter.format(row.usagePct)}%` : numberFormatter.format(row[key]);

  return (
    <div className="analytics-panel">
      <h3>{title}</h3>
      {sub && <p className="analytics-panel-sub">{sub}</p>}
      {rows.length === 0 ? (
        <p className="analytics-coming-soon">{emptyMessage}</p>
      ) : (
        <div className="analytics-tech-table-container">
          <table className="analytics-tech-table">
            <thead>
              <tr>
                <th>{t('analytics.spareParts.columns.productCode')}</th>
                <th>{t('analytics.spareParts.columns.description')}</th>
                <th className="n">{t(METRIC_LABEL_KEY[metricKey])}</th>
                <th className="n">{t(METRIC_LABEL_KEY[secondaryKey])}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.productCode}>
                  <td>{row.productCode}</td>
                  <td>{descriptionFor(row)}</td>
                  <td className="n">{formatValue(row, metricKey)}</td>
                  <td className="n">{formatValue(row, secondaryKey)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export const SparePartsTab: React.FC<SparePartsTabProps> = ({ year }) => {
  const { t, i18n } = useTranslation();
  const { spareParts, loading, error } = useAnalyticsSpareParts(year);

  const numberFormatter = new Intl.NumberFormat(i18n.language);
  const pctFormatter = new Intl.NumberFormat(i18n.language, {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  });

  if (loading) {
    return <p className="analytics-coming-soon">{t('app.loading')}</p>;
  }

  if (error || !spareParts) {
    return <p className="analytics-error">{t('analytics.loadError')}</p>;
  }

  const { kpis, topUsed, topSent, topUsagePct, bottomUsagePct, pctThreshold } = spareParts;

  const descriptionFor = (row: SparePartRankingRow): string => {
    const isItalian = i18n.language.startsWith('it');
    const primary = isItalian ? row.descriptionIt : row.descriptionEn;
    const fallback = isItalian ? row.descriptionEn : row.descriptionIt;
    return primary ?? fallback ?? '—';
  };

  return (
    <div className="analytics-spare-parts">
      <div className="analytics-kpi-cards">
        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">{t('analytics.spareParts.kpis.sent')}</div>
          <div className="analytics-kpi-value">{numberFormatter.format(kpis.sent)}</div>
        </div>
        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">{t('analytics.spareParts.kpis.used')}</div>
          <div className="analytics-kpi-value">{numberFormatter.format(kpis.used)}</div>
        </div>
        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">{t('analytics.spareParts.kpis.usageRatePct')}</div>
          <div className="analytics-kpi-value">{pctFormatter.format(kpis.usageRatePct)}%</div>
        </div>
        <div className="analytics-kpi-card">
          <div className="analytics-kpi-label">{t('analytics.spareParts.kpis.distinctParts')}</div>
          <div className="analytics-kpi-value">{numberFormatter.format(kpis.distinctParts)}</div>
        </div>
      </div>

      <CollapsibleNote title={t('analytics.spareParts.thresholdTitle')}>
        {t('analytics.spareParts.thresholdBanner', { threshold: pctThreshold })}
      </CollapsibleNote>

      <div className="analytics-panels-grid">
        <RankingPanel
          title={t('analytics.spareParts.rankings.topUsed')}
          rows={topUsed}
          metricKey="used"
          secondaryKey="sent"
          emptyMessage={t('analytics.spareParts.noData')}
          descriptionFor={descriptionFor}
          numberFormatter={numberFormatter}
          pctFormatter={pctFormatter}
        />
        <RankingPanel
          title={t('analytics.spareParts.rankings.topSent')}
          rows={topSent}
          metricKey="sent"
          secondaryKey="used"
          emptyMessage={t('analytics.spareParts.noData')}
          descriptionFor={descriptionFor}
          numberFormatter={numberFormatter}
          pctFormatter={pctFormatter}
        />
        <RankingPanel
          title={t('analytics.spareParts.rankings.topUsagePct', { threshold: pctThreshold })}
          sub={t('analytics.spareParts.topUsagePctSub')}
          rows={topUsagePct}
          metricKey="usagePct"
          secondaryKey="sent"
          emptyMessage={t('analytics.spareParts.noData')}
          descriptionFor={descriptionFor}
          numberFormatter={numberFormatter}
          pctFormatter={pctFormatter}
        />
        <RankingPanel
          title={t('analytics.spareParts.rankings.bottomUsagePct', { threshold: pctThreshold })}
          sub={t('analytics.spareParts.bottomUsagePctSub')}
          rows={bottomUsagePct}
          metricKey="usagePct"
          secondaryKey="sent"
          emptyMessage={t('analytics.spareParts.noData')}
          descriptionFor={descriptionFor}
          numberFormatter={numberFormatter}
          pctFormatter={pctFormatter}
        />
      </div>
    </div>
  );
};

export default SparePartsTab;
